import cn from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';

import s from './BaseClickable.module.scss';
import {
  ActionKind,
  ActionParams,
  isWithComplexOnClick,
  ValueProp,
} from './types';

/** Пропсы BaseClickable */
export type BaseClickableProps<V extends ValueProp> = {
  className?: string;

  /** Делает display: block */
  block?: boolean;

  /** Добавляет анимацию при наведении */
  withHover?: boolean;

  /** Добавляет cursor: pointer */
  withCursorPointer?: boolean;

  /** Действие, которое нужно выполнить при клике */
  action?: ActionParams<V>;

  children?: React.ReactNode;

  elementRef?: React.RefObject<HTMLElement>;
};

/**
 * Базовый компонент для всех кликабельных элементов: ссылок, кнопок и т.д.
 */
const BaseClickable = <V extends ValueProp>({
  children,
  className,
  block,
  withHover,
  withCursorPointer,
  action,
  elementRef,
}: BaseClickableProps<V>): React.ReactElement => {
  const handlerClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!isWithComplexOnClick<V>(action) || action.disabled) {
        return;
      }

      if ('value' in action.complexOnClick) {
        e.preventDefault();
        e.stopPropagation();
        action.complexOnClick.onChange(action.complexOnClick.value);

        return;
      }

      if ('onClick' in action.complexOnClick) {
        action.complexOnClick.onClick(e);
      }
    },
    [action]
  );

  const commonProps = {
    className: cn(
      s.base,
      block && s.base_block,
      withHover && s['base_with-hover'],
      withCursorPointer && s.base_withCursorPointer,
      className
    ),
    children,
  };

  if (!action || action.disabled) {
    return (
      <div
        {...commonProps}
        ref={elementRef as React.RefObject<HTMLDivElement>}
      />
    );
  }

  if (action.actionKind === ActionKind.link) {
    // Удаляем из экшена параметр, чтобы он не попал в HTML-тег
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { actionKind: _, ...actionProps } = action;

    return (
      <Link
        ref={elementRef as React.RefObject<HTMLAnchorElement>}
        className={commonProps.className}
        aria-disabled={actionProps.disabled}
        {...actionProps}
      >
        {commonProps.children}
      </Link>
    );
  }

  if (action.actionKind === ActionKind.anchor) {
    // Удаляем из экшена параметр, чтобы он не попал в HTML-тег
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { actionKind: _, ...actionProps } = action;

    return (
      <a
        aria-disabled={actionProps.disabled}
        target="_blank"
        rel="noopener noreferrer nofollow"
        {...actionProps}
        {...commonProps}
        ref={elementRef as React.RefObject<HTMLAnchorElement>}
      />
    );
  }

  const Element = action.actionKind === ActionKind.button ? 'button' : 'div';

  if (isWithComplexOnClick(action)) {
    // Удаляем из экшена лишние параметры, чтобы они не попал в HTML-тег
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { actionKind: _1, complexOnClick: _2, ...actionProps } = action;

    return (
      // @ts-ignore
      <Element
        {...commonProps}
        {...actionProps}
        onClick={handlerClick}
        ref={elementRef as React.RefObject<any>}
      />
    );
  }

  // Удаляем из экшена лишний параметр, чтобы он не попал в HTML-тег
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { actionKind: _, ...actionProps } = action;

  return (
    // @ts-ignore
    <Element
      {...commonProps}
      {...actionProps}
      ref={elementRef as React.RefObject<any>}
    />
  );
};

export default BaseClickable;
